import { supabase } from './client'
import { requestEmailVerificationOTP, verifyEmailVerificationOTP } from './emailVerification.service'

export interface PatientSignupData {
  email: string
  password: string
  name: string
  phoneNumber: string
  dateOfBirth: string
  gender: string
}

export interface PatientLoginData {
  email: string
  password: string
}

export interface Patient {
  id: string
  auth_id: string
  name: string
  phone_number: string
  email_verified: boolean
  verification_status: 'pending' | 'approved' | 'verified' | 'rejected' | null
  profile_image_url: string | null
  is_active: boolean
  is_deleted: boolean
  created_at: string
  email?: string
}

export interface PatientAuthResponse {
  success: boolean
  message?: string
  error?: string
  patient?: Patient & { email?: string }
}

export const patientAuthService = {
  async signup(data: PatientSignupData): Promise<PatientAuthResponse> {
    const { data: authData, error: authError } = await supabase.auth.signUp({
      email: data.email,
      password: data.password,
      options: {
        data: { role: 'patient' },
      },
    })

    if (authError) {
      return { success: false, error: authError.message }
    }

    if (!authData.user) {
      return { success: false, error: 'Failed to create account' }
    }

    // Insert patient record
    const { data: patientRecord, error: patientError } = await supabase.from('patients').insert({
      auth_id: authData.user.id,
      name: data.name,
      phone_number: data.phoneNumber,
      date_of_birth: data.dateOfBirth,
      gender: data.gender,
    }).select('id').single()

    if (patientError) {
      return { success: false, error: patientError.message }
    }

    // Create wallet and settings (best-effort, non-blocking)
    if (patientRecord?.id) {
      try { await supabase.from('patient_wallet').insert({ patient_id: patientRecord.id }) } catch { /* ignore */ }
      try { await supabase.from('patient_settings').insert({ patient_id: patientRecord.id }) } catch { /* ignore */ }
    }

    if (authData.user.email) {
      const verificationResult = await requestEmailVerificationOTP(authData.user.email, 'patient')
      if (!verificationResult.success) {
        return { success: false, error: verificationResult.error }
      }
    }

    return { success: true, message: 'Account created. Please verify your email.' }
  },

  async login(data: PatientLoginData): Promise<PatientAuthResponse> {
    const { data: authData, error: authError } = await supabase.auth.signInWithPassword({
      email: data.email,
      password: data.password,
    })

    if (authError) {
      return { success: false, error: authError.message }
    }

    if (!authData.user) {
      return { success: false, error: 'Login failed' }
    }

    // Fetch patient record
    const { data: patient, error: patientError } = await supabase
      .from('patients')
      .select('*')
      .eq('auth_id', authData.user.id)
      .single()

    if (patientError || !patient) {
      await supabase.auth.signOut()
      return { success: false, error: 'Invalid login details' }
    }

    if (patient.is_deleted) {
      await supabase.auth.signOut()
      return { success: false, error: 'Invalid login details' }
    }

    if (!patient.is_active) {
      await supabase.auth.signOut()
      return { success: false, error: 'Invalid login details' }
    }

    return {
      success: true,
      patient: { ...patient, email: authData.user.email },
    }
  },

  async logout(): Promise<void> {
    await supabase.auth.signOut()
  },

  async getProfile(): Promise<Patient | null> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return null

    const { data: patient } = await supabase
      .from('patients')
      .select('*')
      .eq('auth_id', user.id)
      .maybeSingle()

    if (!patient) return null
    return { ...patient, email: user.email }
  },

  async verifyEmail(email: string, token: string): Promise<PatientAuthResponse> {
    const result = await verifyEmailVerificationOTP(email, token, 'patient')
    return result.success
      ? { success: true, message: 'Email verified successfully' }
      : { success: false, error: result.error }
  },

  async resendVerificationEmail(email: string): Promise<PatientAuthResponse> {
    const result = await requestEmailVerificationOTP(email, 'patient')
    return result.success
      ? { success: true, message: 'Verification email resent' }
      : { success: false, error: result.error }
  },

  async submitIdentityDocument(
    _documentType: 'passport' | 'voters-card' | 'nin-slip',
    file: File
  ): Promise<PatientAuthResponse> {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) return { success: false, error: 'Not authenticated' }

    const fileExt = file.name.split('.').pop()
    const fileName = `${Date.now()}.${fileExt}`
    const filePath = `${user.id}/${fileName}`

    const { error: uploadError } = await supabase.storage
      .from('patient-documents')
      .upload(filePath, file, { contentType: file.type })

    if (uploadError) return { success: false, error: uploadError.message }

    // The live schema tracks patient verification via verification_status only.
    const { error: updateError } = await supabase
      .from('patients')
      .update({
        verification_status: 'pending',
      })
      .eq('auth_id', user.id)

    if (updateError) return { success: false, error: updateError.message }

    return { success: true, message: 'Document uploaded successfully' }
  },

  async getVerificationStatus(): Promise<'pending' | 'approved' | 'verified' | 'rejected' | null> {
    const profile = await this.getProfile()
    return profile?.verification_status ?? null
  },

  async sendPasswordResetEmail(email: string): Promise<PatientAuthResponse> {
    const { error } = await supabase.auth.resetPasswordForEmail(email)
    if (error) return { success: false, error: error.message }
    return { success: true, message: 'Password reset code sent' }
  },

  async verifyPasswordResetOtp(email: string, token: string): Promise<PatientAuthResponse> {
    const { error } = await supabase.auth.verifyOtp({
      email,
      token,
      type: 'recovery',
    })
    if (error) return { success: false, error: error.message }
    return { success: true, message: 'Code verified successfully' }
  },

  async updatePassword(newPassword: string): Promise<PatientAuthResponse> {
    const { error } = await supabase.auth.updateUser({ password: newPassword })
    if (error) return { success: false, error: error.message }
    return { success: true, message: 'Password updated successfully' }
  },
}
